import React, { useState } from "react";
import axios from "axios";
import Icon from "../../../components/AppIcon";


const BookingSummary = ({
  selectedService,
  selectedDate,
  selectedTime,
  formData,
  isSticky = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null); // null, 'success', or 'error'
  const [bookingData, setBookingData] = useState(null);

  // ✅ Format date for display
  const formatDate = (date) => {
    if (!date) return "Not selected";
    try {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return String(date);
    }
  };

  // ✅ Calculate service total
  const calculateTotal = () => {
    if (!selectedService) return 0;
    return selectedService.price || 0;
  };

  // ✅ Check if form is complete
  const isComplete = Boolean(
    selectedService &&
      selectedDate &&
      selectedTime &&
      formData?.firstName &&
      formData?.email &&
      formData?.phone &&
      formData?.location
  );

  // ✅ Save booking to backend
const handleConfirmBooking = async () => {
  // Validate all required fields before sending
  if (
    !selectedService ||
    !selectedDate ||
    !selectedTime ||
    !formData?.firstName ||
    !formData?.email
  ) {
    console.error("❌ Missing required fields frontend validation:");
    console.error("selectedService:", selectedService);
    console.error("selectedDate:", selectedDate);
    console.error("selectedTime:", selectedTime);
    console.error("formData:", formData);
    setBookingStatus("error");
    return;
  }

  const payload = {
    serviceName: selectedService.name,
    serviceDuration: selectedService.duration,
    servicePrice: selectedService.price,
    date: new Date(selectedDate).toISOString().split("T")[0],
    time: selectedTime.time,
    firstName: formData.firstName,
    lastName: formData.lastName || "",
    email: formData.email,
    phone: formData.phone || "",
    location: formData.location || "",
  };

  console.log("📦 Sending payload:", JSON.stringify(payload, null, 2));

  // ✅ Decide API base URL based on environment
  const API_BASE_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:5454"
      : "https://studio-musu-photography.onrender.com";

  try {
    setLoading(true);
    const res = await axios.post(`${API_BASE_URL}/api/bookings`, payload, {
      headers: { "Content-Type": "application/json" },
    });
    console.log("✅ Booking saved:", res.data);
    setBookingStatus("success");
    setBookingData(res.data.booking);
  } catch (error) {
    console.error("❌ Booking save error:", error.response?.data || error.message);
    console.error("Full error:", error);
    setBookingStatus("error");
  } finally {
    setLoading(false);
  }
};


  // Reset booking status
  const resetBooking = () => {
    setBookingStatus(null);
    setBookingData(null);
  };

  return (
    <div
      className={`rounded-2xl shadow-lg p-6 border transition-all duration-300 ${
        isSticky ? "sticky top-24" : ""
      } bg-gradient-to-br from-indigo-50 via-white to-blue-50 border-blue-200`}
    >
 <div className="flex items-center gap-2 mb-4">
  <span className="text-xl">🏠</span>
  <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
    Booking Summary
  </h3>
</div>


      {/* Status Messages */}
      {bookingStatus === 'success' && (
        <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 rounded-xl border border-green-200">
          <div className="flex items-center gap-3 mb-2">
            {/* <Icon name="check-circle" className="w-6 h-6 text-green-600" /> */}
            <span className="font-semibold">✅ Booking Confirmed Successfully!</span>
          </div>
          <p className="text-sm mt-2">We've sent a confirmation to your email. Your booking reference is: 
            <span className="font-mono bg-green-100 px-2 py-1 rounded ml-1">{bookingData?._id?.substring(0, 8) || 'N/A'}</span>
          </p>
          <button
            onClick={resetBooking}
            className="mt-3 text-sm text-green-700 hover:text-green-900 underline"
          >
            Book Another Session
          </button>
        </div>
      )}
      
      {bookingStatus === 'error' && (
        <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 text-red-800 rounded-xl border border-red-200">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="alert-circle" className="w-6 h-6 text-red-600" />
            <span className="font-semibold">Booking Failed</span>
          </div>
          <p className="text-sm">Please check your information and try again. If the problem persists, contact support.</p>
          <button
            onClick={resetBooking}
            className="mt-3 text-sm text-red-700 hover:text-red-900 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Service Details */}
      <div className="space-y-3 text-gray-700 mb-4">
        <div className="flex justify-between py-2 px-3 rounded-lg bg-blue-50/50">
          <span className="text-blue-700 font-medium">Service:</span>
          <span className="font-semibold text-blue-900">
            {selectedService?.name || "Not selected"}
          </span>
        </div>
        <div className="flex justify-between py-2 px-3 rounded-lg bg-indigo-50/50">
          <span className="text-indigo-700 font-medium">Duration:</span>
          <span className="font-semibold text-indigo-900">
            {selectedService?.duration || "-"}
          </span>
        </div>
        <div className="flex justify-between py-2 px-3 rounded-lg bg-purple-50/50">
          <span className="text-purple-700 font-medium">Date:</span>
          <span className="font-semibold text-purple-900">{formatDate(selectedDate)}</span>
        </div>
        <div className="flex justify-between py-2 px-3 rounded-lg bg-violet-50/50">
          <span className="text-violet-700 font-medium">Time:</span>
          <span className="font-semibold text-violet-900">
            {selectedTime?.time || "Not selected"}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className="my-5 border-t border-blue-200/60" />

      {/* Customer Details */}
      <div className="space-y-3 text-gray-700 mb-4">
        <div className="flex justify-between py-2 px-3 rounded-lg bg-slate-50/50">
          <span className="text-slate-700 font-medium">Name:</span>
          <span className="font-semibold text-slate-900">
            {formData?.firstName || "—"} {formData?.lastName || ""}
          </span>
        </div>
        <div className="flex justify-between py-2 px-3 rounded-lg bg-slate-50/50">
          <span className="text-slate-700 font-medium">Email:</span>
          <span className="font-semibold text-slate-900">{formData?.email || "—"}</span>
        </div>
        <div className="flex justify-between py-2 px-3 rounded-lg bg-slate-50/50">
          <span className="text-slate-700 font-medium">Phone:</span>
          <span className="font-semibold text-slate-900">{formData?.phone || "—"}</span>
        </div>
        <div className="flex justify-between py-2 px-3 rounded-lg bg-slate-50/50">
          <span className="text-slate-700 font-medium">Location:</span>
          <span className="font-semibold text-slate-900">{formData?.location || "—"}</span>
        </div>
      </div>

      {/* Divider */}
      <div className="my-5 border-t border-blue-200/60" />

      {/* Total */}
      <div className="flex justify-between font-bold text-lg py-3 px-4 rounded-xl bg-gradient-to-r from-blue-100 to-purple-100">
        <span className="text-blue-800">Total Amount:</span>
        <span className="text-purple-800">₹{calculateTotal()}</span>
      </div>

      {/* Confirm Button */}
      {isComplete && bookingStatus !== 'success' && (
        <button
          onClick={handleConfirmBooking}
          disabled={loading}
          className={`w-full mt-6 py-3 px-4 rounded-xl font-semibold shadow-lg transition-all duration-300 ${
            loading
              ? "bg-gradient-to-r from-blue-300 to-purple-300 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white hover:shadow-xl transform hover:-translate-y-0.5"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Icon name="loader" className="w-5 h-5 animate-spin" />
              Processing Booking...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Icon name="check-circle" className="w-5 h-5" />
              Confirm Booking
            </span>
          )}
        </button>
      )}

      {!isComplete && bookingStatus !== 'success' && (
        <div className="mt-6 p-3 bg-amber-50 text-amber-800 rounded-lg border border-amber-200 text-center">
          <p className="text-sm">Complete all fields to confirm your booking</p>
        </div>
      )}
    </div>
  );
};

export default BookingSummary;

